import { Injectable } from '@angular/core';
import {
    DocumentSnapshot,
    QueryConstraint,
    collection,
    getDocs,
    limit,
    orderBy,
    query,
    startAfter,
    where,
} from 'firebase/firestore';
import { FSSnap } from '../utils/firestore/fs-snap';
import { JobInstance } from './data/models/jobs/job-instance';
import { OrchestratorInstance } from './data/models/jobs/orchestrator-instance';
import { Status } from './data/models/jobs/status';
import { FirestoreService } from './firestore.service';
import { DashboardView } from './query-context/models/dashboard-view.model';
import { IQueryContext } from './query-context/models/IQueryContext';
import { AuthService } from './auth.service';

const OrchestratorCollection = 'Orchestrators';
const JobsCollection = 'Jobs';
const SavedContextsCollection = 'savedContexts';

@Injectable({
    providedIn: 'root',
})
export class DataService {
    constructor(
        private firestore: FirestoreService,
        private auth: AuthService
    ) {}

    async getSavedDashboardView(
        contextId: string
    ): Promise<DashboardView | undefined> {
        const snap = await FirestoreService.getSnap<DashboardView>(
            this.firestore.defaultDb,
            `${SavedContextsCollection}/${contextId}`
        );
        if (!snap.snap.exists()) {
            return undefined;
        }
        snap.data.id = snap.snap.id;
        // if in data.context.datasourceColumns is item with object state.value.start or end with method toDate then call its toDate() method
        if (snap.data['context'].datasourceColumns) {
            snap.data['context'].datasourceColumns.forEach((column: any) => {
                if (
                    column.state &&
                    column.state.value &&
                    column.state.value.start &&
                    column.state.value.start.toDate
                ) {
                    column.state.value.start =
                        column.state.value.start.toDate();
                }
                if (
                    column.state &&
                    column.state.value &&
                    column.state.value.end &&
                    column.state.value.end.toDate
                ) {
                    column.state.value.end = column.state.value.end.toDate();
                }
            });
        }

        return snap.data;
    }

    async setSavedContext<T extends { [x: string]: any }>(
        contextId: string,
        context: T
    ) {
        return await FirestoreService.setDoc(
            this.firestore.defaultDb,
            `${SavedContextsCollection}/${contextId}`,
            context
        );
    }

    /**
     * Adds new contexts to the saved contexts collection.
     * Creates random id for the context.
     */
    async addSavedContext(context: any) {
        return await FirestoreService.addDoc<DashboardView>(
            this.firestore.defaultDb,
            SavedContextsCollection,
            context
        );
    }

    /**
     * Gets named contexts for the current user.
     * @param currentUserEmail
     * @returns List of named contexts.
     */
    async getNamedContexts(options?: {
        dashboardId?: string;
    }): Promise<DashboardView[]> {
        const currentUserEmail = this.auth.auth.currentUser?.email || null;

        let dashboardIdWhere: QueryConstraint[] = [];
        if (options?.dashboardId) {
            // add dashboardId constraint
            dashboardIdWhere.push(
                where('dashboardId', '==', options.dashboardId)
            );
        }

        const privateContextsQuery = query(
            collection(this.firestore.defaultDb, SavedContextsCollection),
            // name must be present
            where('name', '!=', null),
            where('userEmail', '==', currentUserEmail),
            ...dashboardIdWhere
        );

        const publicContextsQuery = query(
            collection(this.firestore.defaultDb, SavedContextsCollection),
            // name must be present
            where('name', '!=', null),
            where('isPublic', '==', true),
            ...dashboardIdWhere
        );

        const [privateSnapshots, publicSnapshots] = await Promise.all([
            getDocs(privateContextsQuery),
            getDocs(publicContextsQuery),
        ]);

        const contexts: DashboardView[] = [];

        privateSnapshots.forEach((doc) => {
            const data = doc.data();
            contexts.push({
                id: doc.id,
                context: data['context'],
                createdAt: data['createdAt'].toDate(),
                appVersion: data['appVersion'],
                name: data['name'] || undefined,
                isPublic: data['isPublic'] || false,
                userEmail: data['userEmail'],
                dashboardId: data['dashboardId'],
            });
        });

        publicSnapshots.forEach((doc) => {
            const data = doc.data();
            // Avoid duplicates if the user's private contexts are also public
            if (data['userEmail'] !== currentUserEmail) {
                contexts.push({
                    id: doc.id,
                    context: data['context'],
                    createdAt: data['createdAt'].toDate(),
                    appVersion: data['appVersion'],
                    name: data['name'] || undefined,
                    isPublic: data['isPublic'] || false,
                    userEmail: data['userEmail'],
                    dashboardId: data['dashboardId'],
                });
            }
        });

        // sort
        contexts.sort((a, b) => {
            if (a.isPublic === b.isPublic) {
                if (a.name === undefined || b.name === undefined) {
                    throw new Error('Context name is required');
                }
                return a.name.localeCompare(b.name);
            }
            return a.isPublic ? -1 : 1;
        });
        return contexts;
    }

    async getCurrentUserNamedContexts(options?: {
        dashboardId?: string;
    }): Promise<DashboardView[]> {
        let dashboardIdWhere: QueryConstraint[] = [];
        if (options?.dashboardId) {
            // add dashboardId constraint
            dashboardIdWhere.push(
                where('dashboardId', '==', options.dashboardId)
            );
        }
        const currentUserEmail = this.auth.auth.currentUser?.email || null;
        const currentUsersContextsQuery = query(
            collection(this.firestore.defaultDb, SavedContextsCollection),
            where('userEmail', '==', currentUserEmail),
            where('name', '!=', null),
            ...dashboardIdWhere
        );
        const snapshots = await getDocs(currentUsersContextsQuery);
        const contexts: DashboardView[] = [];

        snapshots.forEach((doc) => {
            const data = doc.data();
            if (data['userEmail'] === currentUserEmail) {
                contexts.push({
                    id: doc.id,
                    context: data['context'],
                    createdAt: data['createdAt'].toDate(),
                    appVersion: data['appVersion'],
                    name: data['name'] || undefined,
                    isPublic: data['isPublic'] || false,
                    userEmail: data['userEmail'],
                    dashboardId: data['dashboardId'],
                });
            }
        });

        return contexts;
    }

    /**
     * Gets BIJobs Orchestrators by State LastUpdateTime.
     * @param startAfterDoc Pagination - optional DocumentSnapshot to start after.
     * @param limitTo Pagination - number of docs to get, 10 by default.
     * @returns Page of orchestrators.
     */
    async getBIOrchestratorsByUpdateTime(
        startAfterDoc?: DocumentSnapshot,
        limitTo: number = 10
    ): Promise<FSSnap<OrchestratorInstance>[]> {
        var queryConstraints: QueryConstraint[] = [
            orderBy('State.LastUpdateTime', 'desc'),
        ];
        if (startAfterDoc) {
            queryConstraints.push(startAfter(startAfterDoc));
        }
        queryConstraints.push(limit(limitTo));
        return await FirestoreService.getSnaps(
            query(
                collection(this.firestore.jobsDb, OrchestratorCollection),
                ...queryConstraints
            )
        );
    }

    /**
     * Gets BIJobs Orchestrator by its id.
     * @param id Orchestrator id.
     * @returns FSSnap of Orchestrator.
     */
    async getBIOrchestratorById(
        id: string
    ): Promise<FSSnap<OrchestratorInstance>> {
        return await FirestoreService.getSnap(
            this.firestore.jobsDb,
            `${OrchestratorCollection}/${id}`
        );
    }

    async getBIJobsByOrchestratorIdAndStatus(
        orchestratorInstanceId: string,
        status: Status,
        limitTo = 100
    ): Promise<FSSnap<JobInstance>[]> {
        return await FirestoreService.getSnaps(
            query(
                collection(this.firestore.jobsDb, JobsCollection),
                where('OrchestratorInstanceId', '==', orchestratorInstanceId),
                where('Result.Status', '==', status),
                orderBy('State.LastUpdateTime', 'desc'),
                limit(limitTo)
            )
        );
    }
}
